import type {
  BrowserEventPayload,
  BrowserPixelAdapter,
  ScriptErrorReporter,
} from "../BrowserPixelAdapter";
import { injectVendorScript } from "../BrowserPixelAdapter";
import { buildUetPid } from "../AdvancedMatching";
import type { MatchIdentity } from "../AdvancedMatching";
import {
  COOKIEBOT_CONSENT_EVENTS,
  readCookiebotMarketingConsent,
  readPageAdConsent,
} from "../PageConsent";
import type { AdConsentState, PageAdConsent } from "../PageConsent";

export { UetAdapter, UET_ADAPTER_KEY, UET_SCRIPT, UET_CONSENT_WAIT_MS };

const UET_ADAPTER_KEY = "uet";
const UET_SCRIPT = "https://bat.bing.com/bat.js";
// Cookie en la que la UET guarda el clic del anuncio (`_uet<msclkid>`). Solo la
// pone con `ad_storage` concedido; `_uetsid`/`_uetvid` las tiene todo visitante.
const UET_CLICK_COOKIE = "_uetmsclkid";
// Lo que se espera, como mucho, a conocer el consentimiento de la página antes
// de arrancar la etiqueta (la propia UET retrasa ~1,5 s su pageLoad).
const UET_CONSENT_WAIT_MS = 1500;
const UET_CONSENT_POLL_MS = 100;

type ConsentState = AdConsentState;

/**
 * Adaptador de la UET de Microsoft Advertising (`bat.js`).
 *
 * Lo que viaja hecho en el manifest: el `pixel_id` es el id de la etiqueta UET
 * (`ti`) y cada evento lleva como nombre la acción del objetivo de conversión
 * de Microsoft, con `{revenue_value, currency}` si hay valor. Particularidades
 * de la red que se resuelven aquí:
 *
 * - **Una cola por etiqueta**: la primera usa `window.uetq`, la del snippet
 *   oficial (es la que actualiza Cookiebot por su cuenta); si `uetq` ya existe
 *   cuando llegamos (la puso el tenant, otra etiqueta), la nuestra usa
 *   `uetq_<ti>` para no mandar nuestros eventos a una etiqueta ajena. Lo que se
 *   empuja antes de que cargue `bat.js` se queda en la cola y la etiqueta lo
 *   procesa al arrancar.
 * - **Página vista automática**: al arrancar se empuja `pageLoad`, como el
 *   snippet. Es lo que alimenta las listas de remarketing; no hay evento de
 *   página vista en el manifest de Microsoft.
 * - **Conversión con `event_id`**: `uetq.push('event', <acción>, {…, event_id})`.
 *   La copia de servidor (Conversions API de la UET) lleva el mismo id con la
 *   misma etiqueta y acción, y Microsoft se queda con una.
 * - **Conversiones mejoradas**: email y teléfono en claro en
 *   `uetq.push('set', {pid: {em, ph}})`; la UET normaliza y hashea.
 * - **Consentimiento** (UET Consent Mode, solo `ad_storage`): sin estado por
 *   defecto la UET da el consentimiento por concedido, así que antes de
 *   arrancarla se le pasa el de la página. Se toma del Consent Mode de Google
 *   (el que pone el banner) o, si no lo hay, de Cookiebot; mientras no se sepa,
 *   se espera hasta `UET_CONSENT_WAIT_MS`. Si la página no tiene ni lo uno ni
 *   lo otro, arranca sin estado, como el snippet oficial. Las decisiones
 *   posteriores del visitante en Cookiebot y `setConsent` se le pasan como
 *   `update`.
 * - **El envío**: con la etiqueta arrancada, el hit del evento sale en unos
 *   15 ms (`bat.bing.com/action/0?…evt=custom&ea=<acción>&event_id=…`, o
 *   `bat.bing.net` sin consentimiento; medido con la UET real en octubre de
 *   2026). `track` devuelve una promesa que se resuelve cuando el navegador da
 *   por terminada esa petición (Resource Timing), para que el optin la espere
 *   antes de navegar. Antes de que la etiqueta arranque no hay nada que
 *   esperar: el evento sale con el arranque.
 */
class UetAdapter implements BrowserPixelAdapter {
  readonly key = UET_ADAPTER_KEY;
  private _loaded = false;
  private _onScriptError: ScriptErrorReporter | null;
  // ti -> nombre de su cola en window
  private _queues: Record<string, string> = {};
  // ti -> lo que dejamos en esa variable (la cola y, tras arrancar, la
  // etiqueta): si alguien la sustituye, no se le empuja nada
  private _owned: Record<string, unknown> = {};
  private _booted: Record<string, boolean> = {};
  private _scriptRequested = false;
  private _scriptReady = false;
  // Lo último que dijo `setConsent` (manda sobre lo que se lea de la página)
  private _explicitConsent: ConsentState | null = null;
  // Lo último que se le ha pasado a la etiqueta (no se repiten updates iguales)
  private _appliedConsent: ConsentState | null = null;
  private _awaitedHits: AwaitedHit[] = [];
  private _hitObserver: PerformanceObserver | null = null;

  constructor(onScriptError?: ScriptErrorReporter | null) {
    this._onScriptError = onScriptError ?? null;
  }

  load(): void {
    if (this._loaded) {
      return;
    }
    this._loaded = true;
    try {
      for (const name of COOKIEBOT_CONSENT_EVENTS) {
        window.addEventListener(name, () => this._relayCookiebotConsent());
      }
    } catch {
      // sin oyentes la etiqueta sigue con el estado con el que arrancó
    }
  }

  /**
   * Una cola por etiqueta; `bat.js` se inyecta una vez y arranca todas. Con
   * identidad, `pid` se fija antes (también en los init posteriores al del
   * optin, que llegan antes de su conversión).
   */
  init(pixelId: string, identity: MatchIdentity | null): void {
    try {
      if (!this._loaded || !pixelId) {
        return;
      }
      const isNew = !this._queues[pixelId];
      if (isNew) {
        this._claimQueue(pixelId);
      }
      const pid = buildUetPid(identity);
      if (Object.keys(pid).length > 0) {
        this._push(pixelId, "set", { pid });
      }
      if (!isNew) {
        return;
      }
      if (!this._scriptRequested) {
        this._scriptRequested = true;
        injectVendorScript(UET_SCRIPT, this.key, this._onScriptError, () => {
          this._scriptReady = true;
          for (const ti of Object.keys(this._queues)) {
            this._bootWhenConsentKnown(ti, Date.now());
          }
        });
      } else if (this._scriptReady) {
        this._bootWhenConsentKnown(pixelId, Date.now());
      }
    } catch {
      // una etiqueta fallida no puede tumbar el resto de píxeles
    }
  }

  track(
    pixelId: string,
    event: BrowserEventPayload,
    eventId: string,
  ): Promise<void> | void {
    try {
      if (!this._queues[pixelId] || !event?.name) {
        return;
      }
      // El observador, antes de empujar el evento: no se le escapa ningún hit
      const sent = this._booted[pixelId]
        ? this._whenHitLeaves(event.name, eventId)
        : undefined;
      this._push(pixelId, "event", event.name, {
        ...(event.params ?? {}),
        event_id: eventId,
      });
      return sent;
    } catch {
      // el disparo de un píxel jamás afecta al lead ni a la navegación
    }
  }

  setConsent(granted: boolean): void {
    try {
      this._explicitConsent = granted ? "granted" : "denied";
      this._pushConsentUpdate(this._explicitConsent);
    } catch {
      // sin consentimiento aplicado, la etiqueta se queda como estaba
    }
  }

  // La UET guarda el clic del anuncio en su cookie al llegar el visitante.
  // Cubre al que no tiene el clic en el almacén del SDK (llegó por otra página
  // del dominio con la etiqueta, o se le purgó el almacenamiento).
  hasOwnClick(): boolean {
    try {
      return document.cookie
        .split(";")
        .some((cookie) => cookie.split("=")[0].trim() === UET_CLICK_COOKIE);
    } catch {
      return false;
    }
  }

  // `uetq` si está libre; si no, una cola propia de la etiqueta.
  private _claimQueue(ti: string): void {
    const scope = window as unknown as Record<string, unknown>;
    const taken = Object.values(this._queues);
    const name =
      scope.uetq === undefined && taken.indexOf("uetq") === -1
        ? "uetq"
        : "uetq_" + ti.replace(/[^0-9A-Za-z_]/g, "");
    if (scope[name] === undefined) {
      scope[name] = [];
    }
    this._queues[ti] = name;
    this._owned[ti] = scope[name];
  }

  private _push(ti: string, ...args: unknown[]): void {
    const current = (window as unknown as Record<string, unknown>)[
      this._queues[ti]
    ];
    if (current === undefined || current !== this._owned[ti]) {
      return;
    }
    (current as { push: (...items: unknown[]) => unknown }).push(...args);
  }

  private _bootWhenConsentKnown(ti: string, startedAt: number): void {
    try {
      if (this._booted[ti]) {
        return;
      }
      const consent = readPageAdConsent();
      if (
        consent === null &&
        this._explicitConsent === null &&
        Date.now() - startedAt < UET_CONSENT_WAIT_MS
      ) {
        setTimeout(
          () => this._bootWhenConsentKnown(ti, startedAt),
          UET_CONSENT_POLL_MS,
        );
        return;
      }
      this._boot(ti, consent);
    } catch {
      // sin arranque no hay etiqueta, pero la página sigue igual
    }
  }

  /**
   * Arranca la etiqueta como el snippet oficial (`new UET({ti, q})` +
   * `pageLoad`), con el consentimiento DELANTE de lo que ya hubiera en la cola.
   */
  private _boot(ti: string, consent: PageAdConsent | null): void {
    const scope = window as unknown as Record<string, unknown>;
    const UET = scope.UET as (new (options: object) => unknown) | undefined;
    const queueName = this._queues[ti];
    if (typeof UET !== "function" || !queueName) {
      return;
    }
    this._booted[ti] = true;
    const existing = scope[queueName];
    if (!Array.isArray(existing) || existing !== this._owned[ti]) {
      // Ya es una etiqueta arrancada, o alguien ha cambiado la cola: no se arranca otra encima
      return;
    }
    const head: unknown[] = [];
    if (consent !== null) {
      head.push("consent", "default", { ad_storage: consent.initial });
      if (consent.current !== consent.initial) {
        head.push("consent", "update", { ad_storage: consent.current });
      }
      this._appliedConsent = consent.current;
    }
    if (
      this._explicitConsent !== null &&
      this._explicitConsent !== this._appliedConsent
    ) {
      head.push("consent", "update", { ad_storage: this._explicitConsent });
      this._appliedConsent = this._explicitConsent;
    }
    const queue = head.concat(existing);
    const tag = new UET({ ti, enableAutoSpaTracking: true, q: queue }) as {
      push: (...items: unknown[]) => unknown;
    };
    scope[queueName] = tag;
    this._owned[ti] = tag;
    tag.push("pageLoad");
  }

  private _relayCookiebotConsent(): void {
    try {
      if (this._explicitConsent !== null) {
        return;
      }
      const consent = readCookiebotMarketingConsent();
      if (consent !== null) {
        this._pushConsentUpdate(consent);
      }
    } catch {
      // sin el relevo, Cookiebot sigue actualizando `uetq` por su cuenta
    }
  }

  private _pushConsentUpdate(state: ConsentState): void {
    if (state === this._appliedConsent) {
      return;
    }
    this._appliedConsent = state;
    for (const ti of Object.keys(this._queues)) {
      this._push(ti, "consent", "update", { ad_storage: state });
    }
  }

  /**
   * Promesa que se resuelve cuando el navegador da por terminada la petición
   * del evento (`…/action/0?…evt=custom…ea=<acción>…event_id=<id>`, igual con
   * y sin consentimiento, solo cambia el host). `undefined` si el navegador no
   * tiene `PerformanceObserver`. Nunca rechaza y nunca caduca: quien espera
   * pone su propio tope.
   */
  private _whenHitLeaves(
    action: string,
    eventId: string,
  ): Promise<void> | undefined {
    try {
      if (!eventId || !this._observeHits()) {
        return undefined;
      }
      return new Promise<void>((resolve) => {
        this._awaitedHits.push({
          action: "ea=" + encodeURIComponent(action),
          eventIds: [
            "event_id=" + encodeURIComponent(eventId),
            "event_id=" + eventId,
          ],
          resolve,
        });
      });
    } catch {
      return undefined;
    }
  }

  private _observeHits(): boolean {
    if (this._hitObserver) {
      return true;
    }
    if (typeof PerformanceObserver !== "function") {
      return false;
    }
    const observer = new PerformanceObserver((list) => {
      try {
        for (const entry of list.getEntries()) {
          this._settleHits(entry.name);
        }
      } catch {
        // una entrada rara no puede tumbar la página
      }
    });
    try {
      observer.observe({ type: "resource" });
    } catch {
      // navegadores anteriores a la opción `type`
      observer.observe({ entryTypes: ["resource"] });
    }
    this._hitObserver = observer;
    return true;
  }

  private _settleHits(url: string): void {
    if (
      typeof url !== "string" ||
      url.indexOf("/action/") === -1 ||
      url.indexOf("evt=custom") === -1
    ) {
      return;
    }
    const waiting: AwaitedHit[] = [];
    for (const hit of this._awaitedHits) {
      const sameEvent = hit.eventIds.some((id) => url.indexOf(id) !== -1);
      if (sameEvent && url.indexOf(hit.action) !== -1) {
        hit.resolve();
      } else {
        waiting.push(hit);
      }
    }
    this._awaitedHits = waiting;
    if (waiting.length === 0 && this._hitObserver) {
      this._hitObserver.disconnect();
      this._hitObserver = null;
    }
  }
}

interface AwaitedHit {
  // `ea=<acción>`: la acción del objetivo
  action: string;
  // `event_id=<id>`, codificado o tal cual
  eventIds: string[];
  resolve: () => void;
}
